import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { AuthRules, SessionPayload, SyncOp, SyncRejection } from '@wr/shared';
import { seal, sealBytes, unseal, unsealBytes, userKey, type Sealed } from './device.js';

/**
 * One person's data on this device (brief section 9: offline first,
 * encrypted per sign-in, nothing lost). Every value is encrypted with the
 * person's key; only record IDs (UUIDs) and outbox bookkeeping are clear.
 *
 * - records: what the device pulled (own records, current event)
 * - outbox: the changes saved here and not yet accepted by the server
 * - meta: the session snapshot (for offline use), the pull cursor
 * - photos: scanned cards not sent yet, sent after their contact (step 2)
 */
type Entity = 'contact' | 'lead' | 'lead_contact' | 'event' | 'event_note' | 'expense' | 'meeting';

interface UserSchema extends DBSchema {
  records: {
    key: string;
    value: { entity: Entity; id: string; sealed: Sealed };
    indexes: { entity: Entity };
  };
  outbox: {
    key: string;
    value: {
      opId: string;
      createdAt: number;
      status: 'pending' | 'rejected';
      attempts: number;
      nextAttemptAt: number;
      error: SyncRejection | 'invalid' | null;
      sealed: Sealed;
    };
    indexes: { createdAt: number };
  };
  meta: { key: string; value: Sealed };
  photos: {
    key: string;
    value: Omit<PhotoItem, 'error'> & { error: string | null; sealed: Sealed };
  };
}

export interface OfflineSession {
  session: Extract<SessionPayload, { state: 'signed_in' }>;
  rules: AuthRules;
}

export interface OutboxItem {
  opId: string;
  createdAt: number;
  status: 'pending' | 'rejected';
  attempts: number;
  nextAttemptAt: number;
  error: string | null;
  op: SyncOp;
}

/** The photo of a receipt is kept as "expense:<id>", next to the scanned cards. */
export const RECEIPT_PREFIX = 'expense:';

/** A scanned card waiting on this device, keyed by its contact (or a receipt, by its prefix). */
export interface PhotoItem {
  contactId: string;
  type: string;
  createdAt: number;
  status: 'pending' | 'rejected';
  attempts: number;
  nextAttemptAt: number;
  error: string | null;
}

export type LocalRecord = Record<string, unknown> & { id: string };

const recordKey = (entity: Entity, id: string) => `${entity}:${id}`;

export class LocalStore {
  private constructor(
    readonly userId: string,
    private readonly db: IDBPDatabase<UserSchema>,
    private readonly key: CryptoKey,
  ) {}

  static async open(userId: string): Promise<LocalStore> {
    const db = await openDB<UserSchema>(`wr-portal-user-${userId}`, 2, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          database.createObjectStore('records').createIndex('entity', 'entity');
          database.createObjectStore('outbox').createIndex('createdAt', 'createdAt');
          database.createObjectStore('meta');
        }
        if (oldVersion < 2) database.createObjectStore('photos');
      },
    });
    return new LocalStore(userId, db, await userKey(userId));
  }

  close(): void {
    this.db.close();
  }

  // --- Session snapshot, for working offline ---

  async saveSession(value: OfflineSession): Promise<void> {
    await this.db.put('meta', await seal(this.key, value), 'session');
  }

  async loadSession(): Promise<OfflineSession | null> {
    const sealed = await this.db.get('meta', 'session');
    return sealed ? unseal<OfflineSession>(this.key, sealed) : null;
  }

  async forgetSession(): Promise<void> {
    await this.db.delete('meta', 'session');
  }

  async getMeta<T>(name: string): Promise<T | null> {
    const sealed = await this.db.get('meta', name);
    return sealed ? unseal<T>(this.key, sealed) : null;
  }

  async setMeta(name: string, value: unknown): Promise<void> {
    await this.db.put('meta', await seal(this.key, value), name);
  }

  // --- Records pulled from the server ---

  async putRecord(entity: Entity, record: LocalRecord): Promise<void> {
    await this.db.put(
      'records',
      { entity, id: record.id, sealed: await seal(this.key, record) },
      recordKey(entity, record.id),
    );
  }

  async deleteRecord(entity: Entity, id: string): Promise<void> {
    await this.db.delete('records', recordKey(entity, id));
  }

  async getRecord(entity: Entity, id: string): Promise<LocalRecord | null> {
    const row = await this.db.get('records', recordKey(entity, id));
    return row ? unseal<LocalRecord>(this.key, row.sealed) : null;
  }

  async listRecords(entity: Entity): Promise<LocalRecord[]> {
    const rows = await this.db.getAllFromIndex('records', 'entity', entity);
    return Promise.all(rows.map((r) => unseal<LocalRecord>(this.key, r.sealed)));
  }

  // --- Outbox: changes saved here first, sent later ---

  /** Saves a change on the device, waiting to be sent. */
  async enqueue(op: SyncOp, now = Date.now()): Promise<void> {
    await this.db.put(
      'outbox',
      {
        opId: op.opId,
        createdAt: now,
        status: 'pending',
        attempts: 0,
        nextAttemptAt: now,
        error: null,
        sealed: await seal(this.key, op),
      },
      op.opId,
    );
    await this.showLocally(op);
  }

  /** The change shows at once in the local records. */
  private async showLocally(op: SyncOp): Promise<void> {
    if (op.action === 'upsert') {
      const current = (await this.getRecord(op.entity, op.id)) ?? { id: op.id };
      await this.putRecord(op.entity, { ...current, ...op.fields });
    } else if (op.action === 'delete') {
      await this.deleteRecord(op.entity, op.id);
    } else {
      // As the server does: one primary per lead; removing it promotes the next.
      const id = `${op.leadId}/${op.contactId}`;
      const others = (await this.listRecords('lead_contact'))
        .filter((l) => l.lead_id === op.leadId && l.id !== id)
        .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
      const current = await this.getRecord('lead_contact', id);
      if (op.action === 'unlink') {
        await this.deleteRecord('lead_contact', id);
        if (current?.is_primary && others[0]) {
          await this.putRecord('lead_contact', { ...others[0], is_primary: true });
        }
      } else {
        const primary =
          op.isPrimary ?? (current ? Boolean(current.is_primary) : others.length === 0);
        if (primary) {
          for (const other of others.filter((l) => l.is_primary)) {
            await this.putRecord('lead_contact', { ...other, is_primary: false });
          }
        }
        await this.putRecord('lead_contact', {
          id,
          lead_id: op.leadId,
          contact_id: op.contactId,
          is_primary: primary,
          position: op.position ?? Number(current?.position ?? others.length),
        });
      }
    }
  }

  /**
   * The portal's data were reset (round 2, staging): the records, the
   * changes and photos made before the reset and the current event go; the
   * session, the business card and the kiosk PIN stay. A change made after
   * the reset stays, to be sent.
   */
  async emptyForReset(resetAt: number): Promise<void> {
    const kept = (await this.outbox()).filter((i) => i.createdAt > resetAt);
    const keep = new Set(kept.map((i) => i.opId));
    const photoKeys = new Set(
      kept.flatMap((i) =>
        i.op.action === 'upsert' ? [i.op.id, `${RECEIPT_PREFIX}${i.op.id}`] : [],
      ),
    );
    for (const opId of await this.db.getAllKeys('outbox')) {
      if (!keep.has(String(opId))) await this.db.delete('outbox', opId);
    }
    for (const key of await this.db.getAllKeys('photos')) {
      if (!photoKeys.has(String(key))) await this.db.delete('photos', key);
    }
    await this.db.clear('records');
    for (const name of ['cursor', 'pulledEventId', 'eventId']) await this.db.delete('meta', name);
    for (const item of kept) await this.showLocally(item.op);
  }

  /** Every change not accepted yet, oldest first (rejected ones included). */
  async outbox(): Promise<OutboxItem[]> {
    const rows = await this.db.getAllFromIndex('outbox', 'createdAt');
    return Promise.all(
      rows.map(async ({ sealed, ...row }) => ({
        ...row,
        op: await unseal<SyncOp>(this.key, sealed),
      })),
    );
  }

  /** Changes and photos still on this device only. */
  async counts(): Promise<{ pending: number; rejected: number }> {
    const tx = this.db.transaction(['outbox', 'photos']);
    const [changes, photos] = await Promise.all([
      tx.objectStore('outbox').getAll(),
      tx.objectStore('photos').getAll(),
    ]);
    const rows = [...changes, ...photos];
    return {
      pending: rows.filter((r) => r.status === 'pending').length,
      rejected: rows.filter((r) => r.status === 'rejected').length,
    };
  }

  /** Accepted by the server: no longer waiting. */
  async remove(opId: string): Promise<void> {
    await this.db.delete('outbox', opId);
  }

  /** Refused by the server: kept, with the reason, never deleted silently. */
  async reject(opId: string, error: string): Promise<void> {
    const row = await this.db.get('outbox', opId);
    if (row)
      await this.db.put(
        'outbox',
        { ...row, status: 'rejected', error: error as SyncRejection },
        opId,
      );
  }

  /** "Send now" or "Try again": every change waiting (or refused) is due at once. */
  async makeDue(opId?: string): Promise<void> {
    const rows = opId ? [await this.db.get('outbox', opId)] : await this.db.getAll('outbox');
    for (const row of rows) {
      if (!row) continue;
      if (opId === undefined && row.status === 'rejected') continue;
      await this.db.put(
        'outbox',
        { ...row, status: 'pending', error: null, nextAttemptAt: 0 },
        row.opId,
      );
    }
    // A photo is named by its contact ID.
    const photos = opId ? [await this.db.get('photos', opId)] : await this.db.getAll('photos');
    for (const row of photos) {
      if (!row) continue;
      if (opId === undefined && row.status === 'rejected') continue;
      await this.db.put(
        'photos',
        { ...row, status: 'pending', error: null, nextAttemptAt: 0 },
        row.contactId,
      );
    }
  }

  async retryLater(opId: string, attempts: number, nextAttemptAt: number): Promise<void> {
    const row = await this.db.get('outbox', opId);
    if (row) await this.db.put('outbox', { ...row, attempts, nextAttemptAt }, opId);
  }

  // --- Photos: scanned cards, sent after their contact ---

  /**
   * Keeps the photo of a scanned card, encrypted, until the server has it.
   * held: not sent until released, while its contact is being saved.
   */
  async savePhoto(contactId: string, photo: Blob, now = Date.now(), held = false): Promise<void> {
    await this.db.put(
      'photos',
      {
        contactId,
        type: photo.type || 'image/jpeg',
        createdAt: now,
        status: 'pending',
        attempts: 0,
        nextAttemptAt: held ? Number.MAX_SAFE_INTEGER : now,
        error: null,
        sealed: await sealBytes(this.key, await photo.arrayBuffer()),
      },
      contactId,
    );
  }

  /** Every photo not sent yet, oldest first, without the image itself. */
  async photos(): Promise<PhotoItem[]> {
    const rows = await this.db.getAll('photos');
    return rows.map(({ sealed: _sealed, ...row }) => row).sort((a, b) => a.createdAt - b.createdAt);
  }

  async photo(contactId: string): Promise<Blob | null> {
    const row = await this.db.get('photos', contactId);
    if (!row) return null;
    return new Blob([await unsealBytes(this.key, row.sealed)], { type: row.type });
  }

  async releasePhoto(contactId: string, now = Date.now()): Promise<void> {
    const row = await this.db.get('photos', contactId);
    if (row) await this.db.put('photos', { ...row, nextAttemptAt: now }, contactId);
  }

  async removePhoto(contactId: string): Promise<void> {
    await this.db.delete('photos', contactId);
  }

  /** Refused by the server: kept, with the reason, never deleted silently. */
  async rejectPhoto(contactId: string, error: string): Promise<void> {
    const row = await this.db.get('photos', contactId);
    if (row) await this.db.put('photos', { ...row, status: 'rejected', error }, contactId);
  }

  async retryPhotoLater(contactId: string, attempts: number, nextAttemptAt: number): Promise<void> {
    const row = await this.db.get('photos', contactId);
    if (row) await this.db.put('photos', { ...row, attempts, nextAttemptAt }, contactId);
  }
}
